import React, { useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useToast } from "@/components/ui/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PlusIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CounterData } from "@/types/pos/counter";
import { ErrorType } from "@/types";
import { DataTableRowActions } from "./data-table-row-actions";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import Papa from "papaparse";
import { useDebounce } from "@/hooks/use-debounce";

import { displayValue } from "@/lib/helper";
import AssignCounter from "../modal/assign-counter";
import CounterForm from "../modal/counter-form";
import {
  useGetCountersQuery,
  useUpdateCountersMutation,
} from "@/services/counterApi";
import TableFilters from "@/components/ui/table/data-table-filter";
import { useGetStaffListQuery, useGetStaffsQuery } from "@/services/staffsApi";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import { Badge } from "@/components/ui/badge";
import { status } from "@/lib/constants/global";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";

const downloadCSV = (data: CounterData[], fileName: string) => {
  const csv = Papa.unparse(data);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
  counter_name?: string;
  staff_id?: number;
  type: string;
}

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "staff",
};

export default function CounterTableView() {
  const pos_count = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).pos_count ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { searchCriteria, query, toggleSortOrder, setSearchCriteria, inputValue, setInputValue } =
    useTableData(initialValue);
  const [action, setAction] = useState<"add" | "edit">("add");
  const { data: staffList } = useGetStaffListQuery(orgId);
  const { data: staffData } = useGetStaffsQuery({ org_id: orgId, query: "" });

  // counter form
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  // assign counter form
  const [assignCounter, setAssignCounter] = useState<boolean>(false);
  // search input
  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilter] = useState<Record<string, any>>({});

  const {
    data: counterList,
    isLoading,
    refetch,
    error,
    isError,
  } = useGetCountersQuery(
    { query: query },
    {
      skip: query == "",
    }
  );

  const [updateCounter] = useUpdateCountersMutation();


  const counterTableData = React.useMemo(() => {
    return Array.isArray(counterList?.data) ? counterList?.data : [];
  }, [counterList]);

  const staffDataList = React.useMemo(() => {
    return Array.isArray(staffData?.data) ? staffData?.data : [];
  }, [staffData]);

  const { toast } = useToast();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [data, setData] = useState<CounterData | undefined>(undefined);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const actionsColumn: ColumnDef<CounterData> = {
    accessorKey: "action",
    header: ({ table }) => <span>Action</span>,
    cell: ({ row }) => (
      <DataTableRowActions
        access={pos_count}
        handleEdit={handleEdit}
        data={row.original}
        refetch={refetch}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  };

  const columns: ColumnDef<CounterData>[] = [
    {
      accessorKey: "name",
      meta: "Counter Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Counter Name"
          sortKey="name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="capitalize cursor-pointer">
                      <span>
                        {displayValue(
                          `${row.original.name}`.length > 15
                            ? `${row.original.name}`.substring(0, 15) + "..."
                            : `${row.original.name}`
                        )}
                      </span>
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="capitalize text-sm">
                      {displayValue(`${row?.original?.name}`)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "staff",
      meta: "Assigned Cashiers",
      header: () => <p className="text-nowrap">Assigned Cashiers</p>,
      cell: ({ row }) => {
        return (
          <Button
            className="h-8"
            onClick={() => handleViewCashier(row?.original)}
          >
            View Cashiers
          </Button>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "is_open",
      meta: "Status",
      header: () => (
        <div className="flex items-center gap-2">
          <p className="text-nowrap">Open/ Close Status</p>
          {/* <button
            className=" size-5 transition-transform duration-1000 text-gray-400 p-0 flex items-center justify-center"
            onClick={() => toggleSortOrder("staff_id")}
          >
            <i
              className={`fa fa-sort   ${searchCriteria.sort_order == "desc" ? "rotate-0" : "rotate-180"}`}
            ></i>
          </button> */}
        </div>
      ),
      cell: ({ row }) => {
        return (
          <div className="flex justify-center ">
            <Badge
              className={`${row.original?.is_open ? "bg-blue-600" : "bg-red-500"} text-white font-medium`}
            >
              {row.original?.is_open ? "Open" : "Close"}
            </Badge>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "staff_id",
      meta: "Used By",
      header: () => <p className="text-nowrap">Staff</p>,
      cell: ({ row }) => {
        const staff = staffDataList.find(
          (staff) => staff.id === row.original.staff_id
        );
        return row.original.staff_id ? (
          <div className="flex flex-col items-left text-ellipsis whitespace-nowrap overflow-hidden">
            <p className="capitalize text-xs">
              {staff?.first_name + " " + (staff?.last_name ?? "")}
            </p>
            <p className="text-xs text-gray-400">{staff?.email}</p>
          </div>
        ) : (
          <p></p>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "status",
      meta: "Status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const value =
          row.original?.status != null ? row.original?.status : "inactive";
        const statusLabel = status.filter((r) => r.value === value)[0];
        const id = Number(row.original.id);

        return (
          <Select
            defaultValue={value}
            onValueChange={(e) => handleStatusChange({ status: e, id: id })}
            disabled={pos_count == "read"}
          >
            <SelectTrigger className="h-8 max-w-36">
              <SelectValue
                placeholder="Status"
                className="text-gray-400 max-w-48 "
              >
                <span className="flex gap-2 items-center">
                  <span
                    className={`${statusLabel?.color} rounded-[50%] w-4 h-4`}
                  ></span>
                  <span>{statusLabel?.label}</span>
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {status.map((item) => (
                <SelectItem key={item.value + ""} value={item.value + ""}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    ...(pos_count !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: counterTableData as CounterData[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  const handleStatusChange = async (payload: {
    status: string;
    id: number;
  }) => {
    console.log({ payload });
    try {
      const resp = await updateCounter(payload).unwrap();
      if (!resp.error) {
        console.log({ resp });
        refetch();
        toast({
          variant: "success",
          title: "Counter Updated Successfully",
        });
      } else {
        throw resp.error;
      }
    } catch (error) {
      console.error("Error", error);
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        console.error("Error", typedError);
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: typedError.data?.detail || typedError.data?.message,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };
  const handleOpen = () => {
    setAction("add");
    setIsDialogOpen(true);
  };

  const handleEdit = (data: CounterData) => {
    setAction("edit");
    setData(data);
    setIsDialogOpen(true);
  };

  const handleViewCashier = (data: CounterData) => {
    setData(data);
    setAssignCounter(true);
  };

  const totalRecords = counterList?.filtered_counts || 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  function handleFilterChange(field: string, value: string | number) {
    setFilter((prev) => ({
      ...prev,
      [field]: value,
    }));
  }
  const filterDisplay = [
    {
      type: "select",
      name: "status",
      label: "Status",
      options: [
        { id: "active", name: "Active" },
        { id: "inactive", name: "Inactive" },
      ],
      function: (value: string) => handleFilterChange("status", value),
    },
    {
      type: "combobox",
      name: "staff_id",
      label: "Staff",
      options: staffList,
      function: (value: string) => handleFilterChange("staff_id", value),
    },
  ];

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-row gap-2 items-center px-3">
        {/* Search — fills available space */}
        <div className="flex-1 min-w-0">
          <TableSearchFilter setSearchCriteria={setSearchCriteria} inputValue={inputValue} setInputValue={setInputValue} />
        </div>

        {/* Buttons — always same row */}
        <div className="flex flex-row gap-2 flex-shrink-0 items-center">
          {pos_count !== "read" && (
            <Button
              className="bg-primary text-sm text-black flex items-center gap-1 h-8 w-8 p-0 sm:w-auto sm:px-2 flex-shrink-0"
              onClick={handleOpen}
            >
              <PlusIcon className="size-4" />
              <span className="hidden sm:inline">Create New</span>
            </Button>
          )}
          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center flex-shrink-0 ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>
      <div className="rounded-none border border-border  ">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          {/* <Table className="w-full overflow-x-scroll"> */}
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            {" "}
            {/* //overflow-y-auto scrollbar-thin */}
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {/* {isLoading ? ( */}
              {false ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={(e) => {
                      if (pos_count !== "read" && (e.target instanceof Element &&
                        !e.target.closest('[role="checkbox"]') && 
                        !e.target.closest('button') && // Prevent edit when clicking buttons
                        (e.target === e.currentTarget || e.target.closest('td')))) {
                        handleEdit(row.original)
                      }
                    }}
                    className={pos_count !== "read" ? "cursor-pointer hover:bg-secondary" : ''}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No counter found.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* pagination */}
      {counterTableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <AssignCounter
        isOpen={assignCounter}
        setOpen={setAssignCounter}
        data={data}
        setData={setData}
        refetch={refetch}
      />

      <CounterForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        action={action}
        setAction={setAction}
        data={data}
        setData={setData}
        refetch={refetch}
      />

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilter}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />
    </div>
  );
}
